import { useState, useEffect, lazy, Suspense, Component } from 'react';
import { objectsAPI, usersAPI, getErrorMessage } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { formatDateTime } from '@/lib/utils';
import { Building2, Plus, MapPin, Settings, Trash2, Edit, Loader2, Globe, Map, Users } from 'lucide-react';
import { toast } from 'sonner';

// Lazy load MapPicker to avoid SSR issues with Leaflet
const MapPicker = lazy(() => import('@/components/MapPicker'));

// 24h time input — no AM/PM
function TimeInput24({ value, onChange, className }) {
  const parts = (value || '00:00').split(':');
  const h = parts[0] || '00';
  const m = parts[1] || '00';
  const setH = (v) => onChange(`${String(v).padStart(2, '0')}:${m}`);
  const setM = (v) => onChange(`${h}:${String(v).padStart(2, '0')}`);
  return (
    <div className={`flex items-center gap-1 h-10 rounded-md bg-wp-dark/30 border border-wp-dark/50 px-3 ${className || ''}`}>
      <input
        type="number" min="0" max="23" value={parseInt(h)}
        onChange={e => setH(Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))}
        className="w-8 bg-transparent text-white text-center text-sm outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <span className="text-wp-light/50 select-none">:</span>
      <input
        type="number" min="0" max="59" value={parseInt(m)}
        onChange={e => setM(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
        className="w-8 bg-transparent text-white text-center text-sm outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
    </div>
  );
}

class MapErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) {
      return (
        <div className="h-[300px] rounded-xl bg-wp-dark/30 border border-red-500/30 flex flex-col items-center justify-center gap-2 text-red-400 text-sm p-4 text-center">
          <span className="text-2xl">⚠</span>
          <span>Map failed to load. Try reopening the dialog.</span>
          <button type="button" onClick={() => this.setState({ error: null })} className="mt-2 px-3 py-1 rounded bg-red-500/10 border border-red-500/20 text-xs hover:bg-red-500/20">Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

class PageErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
          <div className="glass-card rounded-2xl p-8 max-w-md text-center space-y-4">
            <div className="text-4xl">⚠️</div>
            <h2 className="text-white text-lg font-semibold">Something went wrong</h2>
            <p className="text-wp-light/50 text-sm">{this.state.error?.message}</p>
            <button
              onClick={() => { this.setState({ error: null }); window.location.reload(); }}
              className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm transition-colors"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const TABS = ['basic', 'geofence', 'rules', 'employees'];

export default function Objects() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [objects, setObjects] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingObject, setEditingObject] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: '',
    object_type: 'permanent',
    timezone: 'Europe/Tallinn',
    address: '',
    latitude: '',
    longitude: '',
    radius: 100,
    polygon: null,
    geofence_mode: 'circle',
    buffer_zone: 10,
    grace_period_minutes: 5,
    pause_enabled: true,
    pause_max_times_per_hour: 3,
    pause_max_minutes_per_shift: 30,
    lunch_enabled: true,
    lunch_mandatory: false,
    lunch_min_duration: 30,
    lunch_max_duration: 60,
    shift_enabled: true,
    shift_start_time: '09:00',
    shift_end_time: '18:00',
    shift_count_early_arrival: false,
    shift_count_overtime: true,
    shift_max_overtime_minutes: 120
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadObjects();
    usersAPI.list().then(res => setAllEmployees(res.data || [])).catch(() => {});
  }, []);

  const loadObjects = async () => {
    try {
      const res = await objectsAPI.list();
      setObjects(res.data);
    } catch (error) {
      console.error('Failed to load objects:', error);
      toast.error(t('msg.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      object_type: 'permanent',
      timezone: 'Europe/Tallinn',
      address: '',
      latitude: '',
      longitude: '',
      radius: 100,
      polygon: null,
      geofence_mode: 'circle',
      buffer_zone: 10,
      grace_period_minutes: 5,
      pause_enabled: true,
      pause_max_times_per_hour: 3,
      pause_max_minutes_per_shift: 30,
      lunch_enabled: true,
      lunch_mandatory: false,
      lunch_min_duration: 30,
      lunch_max_duration: 60,
      shift_enabled: true,
      shift_start_time: '09:00',
      shift_end_time: '18:00',
      shift_count_early_arrival: false,
      shift_count_overtime: true,
      shift_max_overtime_minutes: 120
    });
    setEditingObject(null);
    setActiveTab('basic');
  };

  const openCreateDialog = () => {
    resetForm();
    setSelectedEmployees([]);
    setDialogOpen(true);
  };

  const openEditDialog = (obj) => {
    const savedMode = obj.geofence?.mode;
    const resolvedMode =
      savedMode === 'building' || savedMode === 'draw' || savedMode === 'circle'
        ? savedMode
        : (obj.geofence?.polygon ? 'draw' : 'circle');
    setEditingObject(obj);
    setActiveTab('basic');
    setFormData({
      name: obj.name,
      object_type: obj.object_type,
      timezone: obj.timezone,
      address: obj.address || '',
      latitude: obj.geofence?.latitude || '',
      longitude: obj.geofence?.longitude || '',
      radius: obj.geofence?.radius || 100,
      polygon: obj.geofence?.polygon || null,
      geofence_mode: resolvedMode,
      buffer_zone: obj.geofence?.buffer_zone || 10,
      grace_period_minutes: obj.rules?.grace_period_minutes || 5,
      pause_enabled: obj.rules?.pause_rules?.enabled ?? true,
      pause_max_times_per_hour: obj.rules?.pause_rules?.max_times_per_hour || 3,
      pause_max_minutes_per_shift: obj.rules?.pause_rules?.max_minutes_per_shift || 30,
      lunch_enabled: obj.rules?.lunch_rules?.enabled ?? true,
      lunch_mandatory: obj.rules?.lunch_rules?.mandatory || false,
      lunch_min_duration: obj.rules?.lunch_rules?.min_duration_minutes || 30,
      lunch_max_duration: obj.rules?.lunch_rules?.max_duration_minutes || 60,
      shift_enabled: obj.rules?.shift_rules?.enabled ?? true,
      shift_start_time: obj.rules?.shift_rules?.start_time || '09:00',
      shift_end_time: obj.rules?.shift_rules?.end_time || '18:00',
      shift_count_early_arrival: obj.rules?.shift_rules?.count_early_arrival || false,
      shift_count_overtime: obj.rules?.shift_rules?.count_overtime ?? true,
      shift_max_overtime_minutes: obj.rules?.shift_rules?.max_overtime_minutes || 120
    });
    setDialogOpen(true);
    setSelectedEmployees(
      allEmployees.filter(e => (e.assigned_objects || []).includes(obj.id)).map(e => e.id)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let lat = parseFloat(formData.latitude);
    let lng = parseFloat(formData.longitude);
    if ((isNaN(lat) || isNaN(lng)) && formData.polygon?.length > 0) {
      lat = formData.polygon.reduce((s, p) => s + p[0], 0) / formData.polygon.length;
      lng = formData.polygon.reduce((s, p) => s + p[1], 0) / formData.polygon.length;
    }

    const payload = {
      name: formData.name,
      object_type: formData.object_type,
      timezone: formData.timezone,
      address: formData.address || null,
      geofence: {
        latitude: isNaN(lat) ? 0 : lat,
        longitude: isNaN(lng) ? 0 : lng,
        radius: parseInt(formData.radius),
        buffer_zone: parseInt(formData.buffer_zone),
        polygon: formData.polygon || null,
        mode: formData.geofence_mode || 'circle'
      },
      rules: {
        grace_period_minutes: parseInt(formData.grace_period_minutes),
        pause_rules: {
          enabled: formData.pause_enabled,
          max_times_per_hour: parseInt(formData.pause_max_times_per_hour),
          max_minutes_per_shift: parseInt(formData.pause_max_minutes_per_shift)
        },
        lunch_rules: {
          enabled: formData.lunch_enabled,
          mandatory: formData.lunch_mandatory,
          min_duration_minutes: parseInt(formData.lunch_min_duration),
          max_duration_minutes: parseInt(formData.lunch_max_duration)
        },
        shift_rules: {
          enabled: formData.shift_enabled,
          start_time: formData.shift_start_time,
          end_time: formData.shift_end_time,
          count_early_arrival: formData.shift_count_early_arrival,
          count_overtime: formData.shift_count_overtime,
          max_overtime_minutes: parseInt(formData.shift_max_overtime_minutes)
        }
      }
    };

    try {
      let savedObjectId;
      if (editingObject) {
        await objectsAPI.update(editingObject.id, payload);
        await objectsAPI.updateGeofence(editingObject.id, payload.geofence);
        savedObjectId = editingObject.id;
        toast.success(t('msg.objectUpdated'));
      } else {
        const res = await objectsAPI.create(payload);
        savedObjectId = res.data?.id;
        toast.success(t('msg.objectCreated'));
      }

      if (savedObjectId) {
        for (const emp of allEmployees) {
          const isSelected = selectedEmployees.includes(emp.id);
          const hasObj = (emp.assigned_objects || []).includes(savedObjectId);
          if (isSelected && !hasObj) {
            await usersAPI.update(emp.id, { assigned_objects: [...(emp.assigned_objects || []), savedObjectId] });
          } else if (!isSelected && hasObj) {
            await usersAPI.update(emp.id, { assigned_objects: (emp.assigned_objects || []).filter(id => id !== savedObjectId) });
          }
        }
        usersAPI.list().then(res => setAllEmployees(res.data || [])).catch(() => {});
      }

      setDialogOpen(false);
      resetForm();
      loadObjects();
    } catch (error) {
      console.error('Failed to save object:', error);
      toast.error(getErrorMessage(error, t('msg.failedUpdate')));
    }
  };

  const handleDelete = async (objId) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    try {
      await objectsAPI.delete(objId);
      toast.success(t('msg.objectDeleted'));
      loadObjects();
    } catch (error) {
      console.error('Failed to delete object:', error);
      toast.error(t('msg.failedDelete'));
    }
  };

  const assignableEmployees = allEmployees.filter((employee) => employee.role === 'employee');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-500" />
          <div className="absolute inset-0 blur-xl bg-wp-medium/30 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <PageErrorBoundary>
    <div className="space-y-8" data-testid="objects-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white font-heading flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-wp-medium/20 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-wp-light" />
            </div>
            {t('objects.title')}
          </h1>
          <p className="text-wp-light/40 mt-2">{t('objects.subtitle')}</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button
              onClick={openCreateDialog}
              className="flex items-center gap-2 px-5 py-2.5 bg-wp-medium hover:bg-wp-medium text-white font-medium rounded-xl transition-all btn-glow"
              data-testid="create-object-button"
            >
              <Plus className="h-4 w-4" />
              {t('objects.addObject')}
            </button>
          </DialogTrigger>

          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-wp-darker border-wp-dark/50">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">
                {editingObject ? t('objects.editObject') : t('objects.createObject')}
              </DialogTitle>
            </DialogHeader>

            {/* Tab bar */}
            <div className="flex gap-1 p-1 bg-wp-dark/40 rounded-xl">
              {TABS.map(tab => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeTab === tab
                      ? 'bg-wp-medium text-white font-medium'
                      : 'text-wp-light/50 hover:text-white'
                  }`}
                >
                  {t(`objects.tab_${tab}`)}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Tab: Basic */}
              <div style={{ display: activeTab === 'basic' ? 'block' : 'none' }}>
                <div className="space-y-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-wp-light" /> {t('objects.basicInfo')}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-wp-light/70">{t('common.name')}</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Office Building A"
                        required
                        className="bg-wp-dark/30 border-wp-dark/50 text-white"
                        data-testid="object-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="type" className="text-wp-light/70">{t('objects.type')}</Label>
                      <Select value={formData.object_type} onValueChange={(v) => setFormData({...formData, object_type: v})}>
                        <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="permanent">{t('objects.permanentType')}</SelectItem>
                          <SelectItem value="temporary">{t('objects.temporaryType')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="timezone" className="text-wp-light/70">{t('common.timezone')}</Label>
                      <Select value={formData.timezone} onValueChange={(v) => setFormData({...formData, timezone: v})}>
                        <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Europe/Tallinn">Europe/Tallinn</SelectItem>
                          <SelectItem value="Europe/Berlin">Europe/Berlin</SelectItem>
                          <SelectItem value="Europe/London">Europe/London</SelectItem>
                          <SelectItem value="UTC">UTC</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address" className="text-wp-light/70">{t('common.address')}</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        placeholder={t('objects.addressPlaceholder')}
                        className="bg-wp-dark/30 border-wp-dark/50 text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Tab: Geofence — always mounted, hidden when inactive */}
              <div style={{ display: activeTab === 'geofence' ? 'block' : 'none' }}>
                <div className="space-y-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Map className="h-4 w-4 text-wp-light" /> {t('objects.geofence')}
                  </h3>
                  <MapErrorBoundary key={dialogOpen ? 'open' : 'closed'}>
                    <Suspense fallback={
                      <div className="h-[300px] rounded-xl bg-wp-dark/30 border border-wp-dark/50 flex items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-wp-light" />
                      </div>
                    }>
                      {dialogOpen && (
                        <MapPicker
                          key={editingObject?.id || 'new'}
                          latitude={formData.latitude}
                          longitude={formData.longitude}
                          radius={formData.radius}
                          polygon={formData.polygon}
                          initialMode={formData.geofence_mode || null}
                          initialAddress={formData.address || ''}
                          visible={activeTab === 'geofence'}
                          onLocationChange={(lat, lng) => setFormData(f => ({...f, latitude: lat, longitude: lng}))}
                          onRadiusChange={(r) => setFormData(f => ({...f, radius: r}))}
                          onPolygonChange={(p) => setFormData(f => ({...f, polygon: p}))}
                          onAddressChange={(a) => setFormData(f => ({...f, address: a}))}
                          onModeChange={(m) => setFormData(f => ({...f, geofence_mode: m}))}
                        />
                      )}
                    </Suspense>
                  </MapErrorBoundary>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-wp-light/70">{t('objects.bufferZone')}</Label>
                      <div className="flex items-center gap-0 rounded-xl border border-wp-dark/50 bg-wp-dark/30 overflow-hidden">
                        <button type="button" onClick={() => setFormData(f => ({...f, buffer_zone: Math.max(0, parseInt(f.buffer_zone||0)-1)}))} className="px-3 py-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors text-lg font-light select-none">−</button>
                        <input type="number" value={formData.buffer_zone} onChange={(e) => setFormData(f => ({...f, buffer_zone: e.target.value}))} className="flex-1 bg-transparent text-white text-center text-sm outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none py-2" data-testid="object-buffer-input" />
                        <button type="button" onClick={() => setFormData(f => ({...f, buffer_zone: parseInt(f.buffer_zone||0)+1}))} className="px-3 py-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors text-lg font-light select-none">+</button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-wp-light/70">{t('objects.gracePeriod')}</Label>
                      <div className="flex items-center gap-0 rounded-xl border border-wp-dark/50 bg-wp-dark/30 overflow-hidden">
                        <button type="button" onClick={() => setFormData(f => ({...f, grace_period_minutes: Math.max(0, parseInt(f.grace_period_minutes||0)-1)}))} className="px-3 py-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors text-lg font-light select-none">−</button>
                        <input type="number" value={formData.grace_period_minutes} onChange={(e) => setFormData(f => ({...f, grace_period_minutes: e.target.value}))} className="flex-1 bg-transparent text-white text-center text-sm outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none py-2" data-testid="object-grace-input" />
                        <button type="button" onClick={() => setFormData(f => ({...f, grace_period_minutes: parseInt(f.grace_period_minutes||0)+1}))} className="px-3 py-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors text-lg font-light select-none">+</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tab: Rules */}
              <div style={{ display: activeTab === 'rules' ? 'block' : 'none' }}>
                <div className="space-y-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Settings className="h-4 w-4 text-wp-light" /> {t('common.rules')}
                  </h3>

                  {/* Pause Rules */}
                  <div className="p-4 rounded-xl border border-wp-dark/50 bg-wp-dark/30 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-white">{t('objects.pauseAllowed')}</Label>
                      <Switch
                        checked={formData.pause_enabled}
                        onCheckedChange={(v) => setFormData({...formData, pause_enabled: v})}
                      />
                    </div>
                    {formData.pause_enabled && (
                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div className="space-y-2">
                          <Label className="text-wp-light/70">{t('objects.maxTimesHour')}</Label>
                          <Input
                            type="number"
                            value={formData.pause_max_times_per_hour}
                            onChange={(e) => setFormData({...formData, pause_max_times_per_hour: e.target.value})}
                            className="bg-wp-dark/30 border-wp-dark/50 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-wp-light/70">{t('objects.maxMinutesShift')}</Label>
                          <Input
                            type="number"
                            value={formData.pause_max_minutes_per_shift}
                            onChange={(e) => setFormData({...formData, pause_max_minutes_per_shift: e.target.value})}
                            className="bg-wp-dark/30 border-wp-dark/50 text-white"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Lunch Rules */}
                  <div className="p-4 rounded-xl border border-wp-dark/50 bg-wp-dark/30 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-white">{t('objects.lunchAllowed')}</Label>
                      <Switch
                        checked={formData.lunch_enabled}
                        onCheckedChange={(v) => setFormData({...formData, lunch_enabled: v})}
                      />
                    </div>
                    {formData.lunch_enabled && (
                      <>
                        <div className="flex items-center justify-between">
                          <Label className="text-white">{t('objects.lunchMandatory')}</Label>
                          <Switch
                            checked={formData.lunch_mandatory}
                            onCheckedChange={(v) => setFormData({...formData, lunch_mandatory: v})}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4 pt-2">
                          <div className="space-y-2">
                            <Label className="text-wp-light/70">{t('objects.minDuration')}</Label>
                            <Input
                              type="number"
                              value={formData.lunch_min_duration}
                              onChange={(e) => setFormData({...formData, lunch_min_duration: e.target.value})}
                              className="bg-wp-dark/30 border-wp-dark/50 text-white"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-wp-light/70">{t('objects.maxDuration')}</Label>
                            <Input
                              type="number"
                              value={formData.lunch_max_duration}
                              onChange={(e) => setFormData({...formData, lunch_max_duration: e.target.value})}
                              className="bg-wp-dark/30 border-wp-dark/50 text-white"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Shift Rules */}
                  <div className="p-4 rounded-xl border border-wp-dark/50 bg-wp-dark/30 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-white">{t('objects.shiftSettings')}</Label>
                      <Switch
                        checked={formData.shift_enabled}
                        onCheckedChange={(v) => setFormData({...formData, shift_enabled: v})}
                      />
                    </div>
                    {formData.shift_enabled && (
                      <>
                        <div className="grid grid-cols-2 gap-4 pt-2">
                          <div className="space-y-2">
                            <Label className="text-wp-light/70">{t('objects.shiftStart')}</Label>
                            <TimeInput24
                              value={formData.shift_start_time}
                              onChange={(v) => setFormData({...formData, shift_start_time: v})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-wp-light/70">{t('objects.shiftEnd')}</Label>
                            <TimeInput24
                              value={formData.shift_end_time}
                              onChange={(v) => setFormData({...formData, shift_end_time: v})}
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-2">
                          <Label className="text-wp-light/70">{t('objects.countEarlyArrival')}</Label>
                          <Switch
                            checked={formData.shift_count_early_arrival}
                            onCheckedChange={(v) => setFormData({...formData, shift_count_early_arrival: v})}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-wp-light/70">{t('objects.countOvertime')}</Label>
                          <Switch
                            checked={formData.shift_count_overtime}
                            onCheckedChange={(v) => setFormData({...formData, shift_count_overtime: v})}
                          />
                        </div>
                        {formData.shift_count_overtime && (
                          <div className="space-y-2 pt-2">
                            <Label className="text-wp-light/70">{t('objects.maxOvertime')}</Label>
                            <Input
                              type="number"
                              value={formData.shift_max_overtime_minutes}
                              onChange={(e) => setFormData({...formData, shift_max_overtime_minutes: e.target.value})}
                              className="bg-wp-dark/30 border-wp-dark/50 text-white"
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Tab: Employees */}
              <div style={{ display: activeTab === 'employees' ? 'block' : 'none' }}>
                <div className="space-y-3">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Users className="h-4 w-4 text-wp-light" />
                    {t('objects.assignEmployees')}
                  </h3>
                  {assignableEmployees.length === 0 ? (
                    <p className="text-wp-light/40 text-sm py-4 text-center">{t('employees.noEmployees')}</p>
                  ) : (
                    <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
                      {assignableEmployees.map(emp => (
                        <label key={emp.id} className="flex items-center gap-3 cursor-pointer group py-2 px-3 rounded-lg hover:bg-wp-dark/30 transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedEmployees.includes(emp.id)}
                            onChange={e => {
                              if (e.target.checked) setSelectedEmployees(prev => [...prev, emp.id]);
                              else setSelectedEmployees(prev => prev.filter(id => id !== emp.id));
                            }}
                            className="h-4 w-4 rounded accent-indigo-500"
                          />
                          <span className="text-wp-light/80 text-sm group-hover:text-white transition-colors">{emp.name}</span>
                          <span className="text-wp-light/30 text-xs ml-auto">{t(`role.${emp.role}`) || emp.role}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t border-wp-dark/30">
                <button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="px-5 py-2.5 bg-wp-dark/30 hover:bg-wp-dark/50 text-white rounded-xl border border-wp-dark/50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-wp-medium hover:bg-wp-medium text-white font-medium rounded-xl transition-all btn-glow"
                  data-testid="save-object-button"
                >
                  {editingObject ? t('common.update') : t('common.create')}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Objects Grid */}
      {objects.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center">
          <Building2 className="h-12 w-12 mx-auto text-white/20 mb-4" />
          <h3 className="text-lg font-medium text-white">{t('objects.noObjectsYet')}</h3>
          <p className="text-wp-light/40 mt-1">{t('objects.createFirst')}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {objects.map((obj) => (
            <div key={obj.id} className="glass-card rounded-2xl p-6 hover-lift object-card" data-testid={`object-card-${obj.id}`}>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-wp-medium/20 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-wp-light" />
                  </div>
                  <h3 className="font-semibold text-white">{obj.name}</h3>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full ${obj.object_type === 'permanent' ? 'bg-wp-medium/20 text-wp-light' : 'bg-amber-500/20 text-amber-400'}`}>
                  {t(`objects.${obj.object_type}Type`)}
                </span>
              </div>

              <div className="space-y-3 text-sm">
                {obj.address && (
                  <p className="text-wp-light/50 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {obj.address}
                  </p>
                )}
                <p className="text-wp-light/50 flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  {obj.timezone}
                </p>
                <p className="text-white/30 text-xs">
                  {t('objects.geofenceRadius')}: {obj.geofence?.radius}м
                </p>
                <p className="text-white/30 text-xs">
                  {t('common.created')}: {formatDateTime(obj.created_at)}
                </p>
              </div>

              <div className="flex gap-2 pt-4 mt-4 border-t border-wp-dark/30">
                <button
                  onClick={() => openEditDialog(obj)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-wp-dark/30 hover:bg-wp-dark/50 text-wp-light/70 hover:text-wp-lightest rounded-xl border border-wp-dark/50 transition-colors"
                  data-testid={`edit-object-${obj.id}`}
                >
                  <Edit className="h-4 w-4" /> {t('common.edit')}
                </button>
                <button
                  onClick={() => handleDelete(obj.id)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 hover:text-rose-300 rounded-xl border border-rose-500/20 transition-colors"
                  data-testid={`delete-object-${obj.id}`}
                >
                  <Trash2 className="h-4 w-4" /> {t('common.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </PageErrorBoundary>
  );
}
